import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FeaturedSlider from '../components/home/FeaturedSlider';
import SubscribeSection from '../components/home/SubscribeSection';
import CategorySection from '../components/home/CategorySection';
import AdBanner from '../components/ads/AdBanner';
import { ROUTES } from '../constants/routes';
import { Book, ChevronRight } from 'lucide-react';
import { fetchFeaturedBareActs, BareAct } from '../services/bareActService';

const HomePage: React.FC = () => {
  const [featuredActs, setFeaturedActs] = useState<BareAct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    document.title = 'Legal Nest - Legal News, Law Judgments, Law Firms News, Law School News';
    
    // Fetch featured bare acts
    const loadFeaturedActs = async () => {
      try {
        setLoading(true);
        const acts = await fetchFeaturedBareActs(3); // Get top 3 featured acts
        setFeaturedActs(acts);
      } catch (error) {
        console.error("Error fetching featured bare acts:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadFeaturedActs();
  }, []);

  return (
    <div>
      <div className="container-custom py-8">
        <FeaturedSlider maxArticles={4} />
        
        {/* Ad after featured slider */}
        <div className="flex justify-center my-8">
          <AdBanner 
            id="home-featured"
            slot="infeed" 
            size="leaderboard"
          />
        </div>
        
        {/* Grid adjusted to full width (removed lg:grid-cols-3 and lg:col-span-2) */}
        <div className="grid grid-cols-1 gap-8">
          <div>
            <CategorySection 
              title="Supreme Court" 
              categorySlug="supreme-court"
            />
            
            {/* In-feed ad */}
            <div className="flex justify-center my-8">
              <AdBanner 
                id="home-supreme"
                slot="infeed" 
                size="rectangle"
              />
            </div>
            
            <CategorySection 
              title="High Courts" 
              categorySlug="high-courts"
            />
            
            <CategorySection 
              title="News Updates" 
              categorySlug="news-updates"
            />
            
            {/* In-feed ad */}
            <div className="flex justify-center my-8">
              <AdBanner 
                id="home-news"
                slot="infeed" 
                size="rectangle"
              />
            </div>
            
            <SubscribeSection />
            
            <CategorySection
              title="International"
              categorySlug="international"
            />
            
            {/* Bare Acts Section - Updated to show featured bare acts */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-serif font-bold">Bare Acts</h2>
                <Link to={ROUTES.BARE_ACTS} className="text-primary-500 hover:underline font-medium">
                  View All
                </Link>
              </div>
              
              {loading ? (
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-4 text-neutral-500">Loading bare acts...</p>
                </div>
              ) : featuredActs.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {featuredActs.map(act => (
                    <Link 
                      to={`/bare-acts/${act.slug}`}
                      key={act.id}
                      className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-2">
                          <Book size={16} className="text-primary-600" />
                        </div>
                        <span className="text-xs font-medium px-2 py-1 bg-neutral-100 text-neutral-600 rounded-full">
                          {act.year}
                        </span>
                      </div>
                      
                      <h3 className="font-serif font-bold mb-2 line-clamp-2">{act.title}</h3>
                      <p className="text-neutral-600 text-sm mb-3 line-clamp-2">
                        {act.description}
                      </p>
                      
                      <div className="text-primary-600 text-sm font-medium flex items-center mt-auto">
                        Read More <ChevronRight size={16} className="ml-1" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <p className="text-lg text-neutral-600">
                    Access comprehensive collection of Indian legal acts and statutes
                  </p>
                  <Link 
                    to={ROUTES.BARE_ACTS}
                    className="btn btn-primary mt-4"
                  >
                    Browse Bare Acts
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;